import React, { useState } from 'react';
/**
 * Governance Rules — Sub-components
 *
 * RuleCard and ThresholdSection used by GovernanceRulesTab.
 *
 * Phase 3 cleanup: legacy RulebookCard (static markdown metadata) removed —
 * the Regelboeken tab now renders LiveRulebookCard from the executable
 * RULEBOOKS_REGISTRY.
 *
 * Canonical location: portals/admin/content/governance/ (DDD — UI in portal)
 */
import { XCircleIcon, AlertTriangleIcon, AlertOctagonIcon, EyeIcon, InfoIcon, ChevronDownIcon, ChevronRightIcon } from 'lucide-react';
import { DEFAULT_POLICY } from '../../../../governance';
import type { Severity, Stage, Rule } from './governanceRulesData';
import { STAGE_LABELS, STAGE_COLORS } from './governanceRulesData';
// ─── Severity Config ──────────────────────────────────────────
// Phase 1 (Strak-Trek): Extended to 5 levels. Existing rules with severity
// 'WARNING' continue to render as the coach/verbeterpunt style (now blue,
// aligned with shared.tsx SEVERITY_CONFIG.coach).
const SEVERITY_CONFIG: Record<Severity, {
  icon: React.ReactNode;
  bg: string;
  text: string;
  border: string;
  label: string;
}> = {
  CRITICAL: {
    icon: <AlertOctagonIcon className="h-4 w-4" data-id="element-3875" />,
    bg: 'bg-red-50',
    text: 'text-red-700',
    border: 'border-red-200',
    label: 'Kritiek'
  },
  BLOCKER: {
    icon: <XCircleIcon className="h-4 w-4" data-id="element-3876" />,
    bg: 'bg-orange-50',
    text: 'text-orange-700',
    border: 'border-orange-200',
    label: 'Blokkerend'
  },
  REVIEW: {
    icon: <EyeIcon className="h-4 w-4" data-id="element-3877" />,
    bg: 'bg-yellow-50',
    text: 'text-yellow-700',
    border: 'border-yellow-200',
    label: 'Review'
  },
  WARNING: {
    icon: <AlertTriangleIcon className="h-4 w-4" data-id="element-3878" />,
    bg: 'bg-blue-50',
    text: 'text-blue-700',
    border: 'border-blue-200',
    label: 'Verbeterpunt'
  },
  INFO: {
    icon: <InfoIcon className="h-4 w-4" data-id="element-3879" />,
    bg: 'bg-slate-50',
    text: 'text-slate-700',
    border: 'border-slate-200',
    label: 'Informatief'
  }
};
// ─── RuleCard ─────────────────────────────────────────────────
export function RuleCard({
  rule
}: {
  rule: Rule;
}) {
  const [expanded, setExpanded] = useState(false);
  const sev = SEVERITY_CONFIG[rule.severity];
  return <div className={`rounded-lg border ${sev.border} bg-white overflow-hidden`} data-id="element-3880">
      <button className="flex w-full items-start gap-3 p-4 text-left transition-colors hover:bg-gray-50" onClick={() => setExpanded(!expanded)} aria-expanded={expanded} data-id="element-3881">
        <div className={`mt-0.5 flex-shrink-0 ${sev.text}`} data-id="element-3882">{sev.icon}</div>
        <div className="min-w-0 flex-1" data-id="element-3883">
          <div className="flex flex-wrap items-center gap-2" data-id="element-3884">
            <code className="text-xs font-mono text-gray-400" data-id="element-3885">{rule.id}</code>
            <span className="font-semibold text-gray-800" data-id="element-3886">{rule.label}</span>
            <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${STAGE_COLORS[rule.stage]}`} data-id="element-3887">
              {STAGE_LABELS[rule.stage]}
            </span>
            <span className={`rounded-full px-2 py-0.5 text-xs font-semibold ${sev.bg} ${sev.text}`} data-id="element-3888">
              {sev.label}
            </span>
          </div>
          <p className="mt-1 text-sm text-gray-600" data-id="element-3889">{rule.description}</p>
        </div>
        <div className="flex-shrink-0 text-gray-400" data-id="element-3890">
          {expanded ? <ChevronDownIcon className="h-4 w-4" data-id="element-3891" /> : <ChevronRightIcon className="h-4 w-4" data-id="element-3892" />}
        </div>
      </button>

      {expanded && <div className={`border-t ${sev.border} ${sev.bg} px-4 py-3 space-y-3`} data-id="element-3893">
          {rule.example && <div data-id="element-3894">
              <p className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-1" data-id="element-3895">
                Bron / Voorbeeld
              </p>
              <code className="block rounded bg-white/70 px-3 py-2 text-xs text-gray-700 border border-gray-200" data-id="element-3896">
                {rule.example}
              </code>
            </div>}
          {rule.fix && <div data-id="element-3897">
              <p className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-1" data-id="element-3898">
                Oplossing
              </p>
              <p className="text-sm text-gray-700" data-id="element-3899">{rule.fix}</p>
            </div>}
        </div>}
    </div>;
}
// ─── ThresholdSection ─────────────────────────────────────────
export function ThresholdSection() {
  const gates = DEFAULT_POLICY.gates;
  const budgets = DEFAULT_POLICY.contentBudgets;
  const flags = DEFAULT_POLICY.featureFlags;
  const thresholdItems = [{
    label: 'Productie min. score',
    value: `${gates.productionMinScore}%`,
    description: 'Vragen onder deze score worden geblokkeerd voor productie (oefenmodus).',
    path: 'gates.productionMinScore',
    color: gates.productionMinScore >= 70 ? 'text-emerald-700' : 'text-amber-700'
  }, {
    label: 'Assessment min. score',
    value: `${gates.assessmentMinScore}%`,
    description: 'Vragen onder deze score zijn niet geschikt voor CITO-stijl toetsen.',
    path: 'gates.assessmentMinScore',
    color: gates.assessmentMinScore >= 80 ? 'text-emerald-700' : 'text-amber-700'
  }, {
    label: 'Review drempel (coach ernst)',
    value: `≥ ${gates.reviewCoachSeverityThreshold}`,
    description: 'Coach-issues met ernst ≥ deze waarde triggeren een review-markering.',
    path: 'gates.reviewCoachSeverityThreshold',
    color: 'text-blue-700'
  }, {
    label: 'Min. vragen per family (assessment)',
    value: `${gates.assessmentMinQuestionsPerFamily}`,
    description: 'Families met minder vragen zijn niet assessment-geschikt.',
    path: 'gates.assessmentMinQuestionsPerFamily',
    color: 'text-blue-700'
  }];
  const budgetItems = [{
    label: 'Min. uitleglengte (basic)',
    value: `${budgets.minExplanationLengthByTierBand.basic} tekens`,
    description: 'Tiers 1–4 (B6/M6)',
    path: 'contentBudgets.minExplanationLengthByTierBand.basic'
  }, {
    label: 'Min. uitleglengte (intermediate)',
    value: `${budgets.minExplanationLengthByTierBand.intermediate} tekens`,
    description: 'Tiers 5–8 (E6/B7)',
    path: 'contentBudgets.minExplanationLengthByTierBand.intermediate'
  }, {
    label: 'Min. uitleglengte (advanced)',
    value: `${budgets.minExplanationLengthByTierBand.advanced} tekens`,
    description: 'Tiers 9–12 (M7/E7)',
    path: 'contentBudgets.minExplanationLengthByTierBand.advanced'
  }, {
    label: 'Min. uitleglengte (expert)',
    value: `${budgets.minExplanationLengthByTierBand.expert} tekens`,
    description: 'Tiers 13–16 (B8/E8+)',
    path: 'contentBudgets.minExplanationLengthByTierBand.expert'
  }, {
    label: 'Min. distractor-uitleg (basic)',
    value: `${budgets.minDistractorLengthByTierBand.basic} tekens`,
    description: 'Tiers 1–4',
    path: 'contentBudgets.minDistractorLengthByTierBand.basic'
  }, {
    label: 'Min. distractor-uitleg (intermediate)',
    value: `${budgets.minDistractorLengthByTierBand.intermediate} tekens`,
    description: 'Tiers 5–8',
    path: 'contentBudgets.minDistractorLengthByTierBand.intermediate'
  }];
  const featureFlagItems = Object.entries(flags).map(([key, value]) => ({
    key,
    value: value as boolean,
    label: key.replace(/([A-Z])/g, ' $1').replace(/^./, s => s.toUpperCase())
  }));
  return <div className="space-y-6" data-id="element-3900">
      {/* Info banner */}
      <div className="rounded-lg border border-blue-200 bg-blue-50 p-4" data-id="element-3901">
        <div className="flex items-start gap-3" data-id="element-3902">
          <InfoIcon className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" data-id="element-3903" />
          <div data-id="element-3904">
            <p className="text-sm font-semibold text-blue-800" data-id="element-3905">
              Drempelwaarden aanpassen
            </p>
            <p className="text-sm text-blue-700 mt-0.5" data-id="element-3906">
              Drempelwaarden zijn gedefinieerd in{' '}
              <code className="font-mono bg-blue-100 px-1 rounded" data-id="element-3907">
                src/governance/pipeline/defaultPolicy.ts
              </code>
              . Pas het bestand direct aan om drempelwaarden te wijzigen.
              Gebruik de <strong data-id="element-3908">Instellingen</strong> tab voor
              runtime-aanpassingen.
            </p>
          </div>
        </div>
      </div>

      {/* Gate Thresholds */}
      <div data-id="element-3909">
        <h3 className="text-sm font-semibold uppercase tracking-wide text-gray-500 mb-3" data-id="element-3910">
          Kwaliteitspoorten
        </h3>
        <div className="rounded-xl border border-gray-100 bg-white overflow-hidden" data-id="element-3911">
          {thresholdItems.map((item, i) => <div key={i} className={`flex items-start justify-between gap-4 px-4 py-3 ${i < thresholdItems.length - 1 ? 'border-b border-gray-50' : ''}`} data-id="element-3912">
              <div className="min-w-0" data-id="element-3913">
                <p className="text-sm font-medium text-gray-800" data-id="element-3914">
                  {item.label}
                </p>
                <p className="text-xs text-gray-500 mt-0.5" data-id="element-3915">
                  {item.description}
                </p>
                <code className="text-xs text-gray-400 font-mono mt-1 block" data-id="element-3916">
                  {item.path}
                </code>
              </div>
              <span className={`text-lg font-bold flex-shrink-0 ${item.color}`} data-id="element-3917">
                {item.value}
              </span>
            </div>)}
        </div>
      </div>

      {/* Content Budgets */}
      <div data-id="element-3918">
        <h3 className="text-sm font-semibold uppercase tracking-wide text-gray-500 mb-3" data-id="element-3919">
          Inhoudsbudgetten
        </h3>
        <div className="rounded-xl border border-gray-100 bg-white overflow-hidden" data-id="element-3920">
          {budgetItems.map((item, i) => <div key={i} className={`flex items-start justify-between gap-4 px-4 py-3 ${i < budgetItems.length - 1 ? 'border-b border-gray-50' : ''}`} data-id="element-3921">
              <div className="min-w-0" data-id="element-3922">
                <p className="text-sm font-medium text-gray-800" data-id="element-3923">
                  {item.label}
                </p>
                <p className="text-xs text-gray-500 mt-0.5" data-id="element-3924">
                  {item.description}
                </p>
                <code className="text-xs text-gray-400 font-mono mt-1 block" data-id="element-3925">
                  {item.path}
                </code>
              </div>
              <span className="text-sm font-bold text-gray-700 flex-shrink-0" data-id="element-3926">
                {item.value}
              </span>
            </div>)}
        </div>
      </div>

      {/* Feature Flags */}
      <div data-id="element-3927">
        <h3 className="text-sm font-semibold uppercase tracking-wide text-gray-500 mb-3" data-id="element-3928">
          Feature Flags
        </h3>
        <div className="rounded-xl border border-gray-100 bg-white overflow-hidden" data-id="element-3929">
          {featureFlagItems.map((item, i) => <div key={item.key} className={`flex items-center justify-between gap-4 px-4 py-3 ${i < featureFlagItems.length - 1 ? 'border-b border-gray-50' : ''}`} data-id="element-3930">
              <div className="min-w-0" data-id="element-3931">
                <p className="text-sm font-medium text-gray-800" data-id="element-3932">
                  {item.label}
                </p>
                <code className="text-xs text-gray-400 font-mono" data-id="element-3933">
                  featureFlags.{item.key}
                </code>
              </div>
              <span className={`rounded-full px-2.5 py-1 text-xs font-semibold flex-shrink-0 ${item.value ? 'bg-emerald-100 text-emerald-700' : 'bg-gray-100 text-gray-500'}`} data-id="element-3934">
                {item.value ? 'Actief' : 'Inactief'}
              </span>
            </div>)}
        </div>
      </div>
    </div>;
}